import React, { useEffect, useState } from "react";
import DashboardLayout from "./DashboardLayout/DasboardLayout";
import { API_URL } from "./config";
import { Calendar, Radio } from "lucide-react";

const CHANNELS = [
  { key: "web_app", label: "Web App" },
  { key: "xgobot", label: "xgoBot (WhatsApp)" },
  { key: "mobile_app", label: "Mobile App" },
  { key: "unknown", label: "Unknown / Legacy" },
];

const TIME_FILTERS = [
  { value: "today", label: "Today", dateRange: 0 },
  { value: "7days", label: "Past 7 Days", dateRange: 7 },
  { value: "14days", label: "Past 14 Days", dateRange: 14 },
  { value: "30days", label: "Past 30 Days", dateRange: 30 },
  { value: "all", label: "All Time", dateRange: 3650 },
];

// Sums a set of per-day rows (each already pivoted by channel via the backend's
// SUM(CASE WHEN...) query) down into one total per channel for the selected window.
function sumByChannel(rows) {
  const totals = { web_app: 0, xgobot: 0, mobile_app: 0, unknown: 0 };
  for (const row of rows) {
    totals.web_app += Number(row.web_app) || 0;
    totals.xgobot += Number(row.xgobot) || 0;
    totals.mobile_app += Number(row.mobile_app) || 0;
    totals.unknown += Number(row.unknown) || 0;
  }
  return totals;
}

// Same idea for the rides-by-channel rows, which aren't pre-pivoted (they come back one row per
// date+channel) — sums requested/accepted_completed per channel across the selected window.
function sumRidesByChannel(rows) {
  const totals = {};
  for (const key of ["web_app", "xgobot", "mobile_app"]) {
    totals[key] = { requested: 0, accepted_completed: 0 };
  }
  totals.unknown = { requested: 0, accepted_completed: 0 };
  for (const row of rows) {
    const key = row.channel && totals[row.channel] ? row.channel : "unknown";
    totals[key].requested += Number(row.requested) || 0;
    totals[key].accepted_completed += Number(row.accepted_completed) || 0;
  }
  return totals;
}

function SimpleChannelTable({ title, totals }) {
  const total = CHANNELS.reduce((sum, c) => sum + (totals[c.key] || 0), 0);
  return (
    <div className="overflow-x-auto shadow-md rounded-lg mb-6">
      <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
        <thead className="text-xs text-gray-700 uppercase bg-gray-100 dark:bg-gray-700 dark:text-gray-300">
          <tr>
            <th className="px-6 py-3" colSpan={2}>
              {title}
            </th>
          </tr>
        </thead>
        <tbody>
          {CHANNELS.map((c) => (
            <tr key={c.key} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
              <td className="px-6 py-3">{c.label}</td>
              <td className="px-6 py-3 font-semibold">{totals[c.key] || 0}</td>
            </tr>
          ))}
          <tr className="bg-gray-50 dark:bg-gray-700 font-bold">
            <td className="px-6 py-3">Total</td>
            <td className="px-6 py-3">{total}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function RideCategoryTable({ title, totals }) {
  return (
    <div className="overflow-x-auto shadow-md rounded-lg mb-6">
      <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300">
        <thead className="text-xs text-gray-700 uppercase bg-gray-100 dark:bg-gray-700 dark:text-gray-300">
          <tr>
            <th className="px-6 py-3" colSpan={3}>
              {title}
            </th>
          </tr>
          <tr>
            <th className="px-6 py-2">Channel</th>
            <th className="px-6 py-2">Requested</th>
            <th className="px-6 py-2">Accepted &amp; Completed</th>
          </tr>
        </thead>
        <tbody>
          {CHANNELS.map((c) => (
            <tr key={c.key} className="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
              <td className="px-6 py-3">{c.label}</td>
              <td className="px-6 py-3">{totals[c.key]?.requested || 0}</td>
              <td className="px-6 py-3 font-semibold">{totals[c.key]?.accepted_completed || 0}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const ChannelAttribution = () => {
  const [timeFilter, setTimeFilter] = useState("today");
  const [signups, setSignups] = useState({});
  const [logins, setLogins] = useState({});
  const [privateRides, setPrivateRides] = useState({});
  const [deliveries, setDeliveries] = useState({});
  const [rideshare, setRideshare] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const dateRange = TIME_FILTERS.find((f) => f.value === timeFilter)?.dateRange ?? 0;
    setLoading(true);

    Promise.all([
      fetch(`${API_URL}/channel-analytics/signups?date_range=${dateRange}`).then((r) => r.json()),
      fetch(`${API_URL}/channel-analytics/logins?date_range=${dateRange}`).then((r) => r.json()),
      fetch(`${API_URL}/channel-analytics/rides?date_range=${dateRange}`).then((r) => r.json()),
    ])
      .then(([signupsRes, loginsRes, ridesRes]) => {
        setSignups(sumByChannel(signupsRes.data || []));
        setLogins(sumByChannel(loginsRes.data || []));
        const rides = ridesRes.data || {};
        setPrivateRides(sumRidesByChannel(rides.private_ride || []));
        setDeliveries(sumRidesByChannel(rides.delivery || []));
        setRideshare(sumRidesByChannel(rides.rideshare || []));
      })
      .catch((error) => console.error("Error fetching channel analytics:", error))
      .finally(() => setLoading(false));
  }, [timeFilter]);

  return (
    <DashboardLayout>
      <div className="p-6 bg-white dark:bg-gray-800 min-h-screen">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
              <Radio size={28} /> Channel Attribution
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Which channel — web app, xgoBot (WhatsApp), or the mobile app — signups, logins, and
              rides came through.
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <Calendar className="text-gray-500" size={20} />
            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
              {TIME_FILTERS.find((f) => f.value === timeFilter)?.label}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 mb-6">
          {TIME_FILTERS.map((filter) => (
            <button
              key={filter.value}
              onClick={() => setTimeFilter(filter.value)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                timeFilter === filter.value
                  ? "bg-blue-600 text-white shadow-lg transform scale-105"
                  : "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600"
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">Loading…</div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <SimpleChannelTable title="Signups" totals={signups} />
              <SimpleChannelTable title="Logins" totals={logins} />
            </div>

            <h2 className="text-xl font-bold text-gray-800 dark:text-white mt-4 mb-4">
              Rides by Channel
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <RideCategoryTable title="Private Rides" totals={privateRides} />
              <RideCategoryTable title="Shared Rides" totals={rideshare} />
              <RideCategoryTable title="Deliveries" totals={deliveries} />
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ChannelAttribution;
